import { generateList } from "@kidslivesafe/sc-foundation";
import {
  Chit,
  Container,
  GoogleStreetViewStatic,
  Icon,
  IconList,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";
import React from "react";

import "./pre-foreclosure-card.css";
import { preForeclosureData } from "../../../features/reports/reportTypes";
import { formatNumberCommas, fullCapsText, parseDate } from "../../../utils";
import { selectBrandInfo } from "../../../features/brand/brandSlice";
import { useAppSelector } from "../../../hooks/hooks";
import { usePropertySearchDispatch } from "../../../hooks/propertyReportHook";
import { MemberEvent } from "../../../features/admin/adminTypes";

interface PreForeclosureCardProps {
  preForeclosureItem: preForeclosureData;
  memberEventMarketSearch: (event: MemberEvent) => void;
}

const PreForeclosureCard: React.FC<PreForeclosureCardProps> = ({
  preForeclosureItem,
  memberEventMarketSearch,
}) => {
  const brandInfo = useAppSelector(selectBrandInfo);
  const pullPropertyReport = usePropertySearchDispatch();

  const renderChit = ({ text }: { text: string }) => {
    switch (text) {
      case "Pre-foreclosure":
        return <Chit unresponsive chitText={text} dotColor="green" />;
      case "Auction Scheduled":
        return <Chit unresponsive chitText={text} dotColor="yellow" />;
      case "Auction Complete":
        return <Chit unresponsive chitText={text + "d"} dotColor="red" />; // BE reasons
    }
  };

  const convertAddressToTitleCase = (str: string) => {
    return str
      .toLowerCase()
      .split(" ")
      .map(word => {
        if (word.length === 2 && /^[a-z]{2}$/.test(word)) {
          return word.toUpperCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  return (
    <Container
      customClassName="pre-foreclosure-card"
      handleClickContainer={() => {
        memberEventMarketSearch({
          EventCategory: "View Foreclosure",
          EventAction: "Button Click",
          MetaData: JSON.stringify({
            source: "list",
          }),
        });
        pullPropertyReport(preForeclosureItem, "market search");
      }}
      altColor="dark-grey"
      border
      borderThickness={2}
      bodyBorderRadius
    >
      <GoogleStreetViewStatic
        lat={preForeclosureItem.latitude}
        long={preForeclosureItem.longitude}
        size={{ height: 189, width: 400 }}
        staticMapFlag
        staticMapProperties={{
          markerColor: "D94242",
          markerSize: "mid",
          zoom: 19,
        }}
      />
      <Container
        altColor="universal-grey"
        customClassName="pre-foreclosure-card__body"
      >
        <div className="pre-foreclosure-card__body__chit-container">
          {renderChit({
            text: preForeclosureItem.propertyPfcType,
          })}
        </div>
        <Padding space="md" top="xl">
          <Layout type="flex" flexProps={{ direction: "column", gap: "sm" }}>
            <Layout type="flex" flexProps={{ direction: "column", gap: "xxs" }}>
              <Text size="xs" tag="i" unresponsive>
                {generateList([
                  {
                    value: preForeclosureItem.lastSoldDate
                      ? parseDate(
                          preForeclosureItem.recordingDate,
                          "MM/DD/YYYY",
                        )
                      : "N/A",
                    label: "Recording Date: ",
                  },
                ])}
              </Text>
              {preForeclosureItem.auctionDate ? (
                <Text size="xs" tag="i" unresponsive>
                  {generateList([
                    {
                      value: preForeclosureItem.auctionDate
                        ? parseDate(
                            preForeclosureItem.auctionDate,
                            "MM/DD/YYYY",
                          )
                        : "N/A",
                      label: "Auction Date: ",
                    },
                  ])}
                </Text>
              ) : (
                <Text size="xs" tag="i" unresponsive>
                  {generateList([
                    {
                      value: preForeclosureItem.lastSoldDate
                        ? parseDate(
                            preForeclosureItem.lastSoldDate,
                            "MM/DD/YYYY",
                          )
                        : "N/A",
                      label: "Last Sold Date: ",
                    },
                  ])}
                </Text>
              )}
            </Layout>

            <Layout type="flex" flexProps={{ direction: "column", gap: "xs" }}>
              <Layout
                type="flex"
                flexProps={{
                  direction: "row",
                  gap: "xs",
                }}
                breakpointStyles={{
                  desktop: {
                    type: "flex",
                    flexProps: {
                      direction: "row",
                      gap: "xs",
                      justifyContent: "space-between",
                    },
                  },
                }}
              >
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "row",
                    gap: "xxs",
                    alignItems: "center",
                  }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        direction: "column",
                        gap: "xxxs",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <Icon name={IconList.IconBed} />
                  <Text unresponsive size="xs">
                    {preForeclosureItem.bedrooms} bds
                  </Text>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "row",
                    gap: "xxs",
                    alignItems: "center",
                  }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        direction: "column",
                        gap: "xxxs",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <Icon name={IconList.IconBath} />
                  <Text unresponsive size="xs">
                    {preForeclosureItem.totalBaths
                      ? preForeclosureItem.totalBaths
                      : "--"}{" "}
                    ba
                  </Text>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "row",
                    gap: "xxs",
                    alignItems: "center",
                  }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        direction: "column",
                        gap: "xxxs",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <Icon name={IconList.IconRightAngleLeft} />
                  <Text unresponsive size="xs">
                    {preForeclosureItem.sumBuildingSqFt &&
                      formatNumberCommas(
                        preForeclosureItem.sumBuildingSqFt,
                      )}{" "}
                    sqft
                  </Text>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{
                    direction: "row",
                    gap: "xxs",
                    alignItems: "center",
                  }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        direction: "column",
                        gap: "xxxs",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <Icon name={IconList.IconFenceDetailed} />
                  <Text unresponsive size="xs">
                    {preForeclosureItem.lotSizeAcres &&
                    preForeclosureItem.lotSizeAcres > 0.25
                      ? formatNumberCommas(
                          preForeclosureItem.lotSizeAcres.toFixed(2),
                        )
                      : preForeclosureItem.lotSizeAcers && //TODO remove when backend is fixed
                          preForeclosureItem.lotSizeAcers > 0.25
                        ? formatNumberCommas(
                            preForeclosureItem.lotSizeAcers.toFixed(2),
                          )
                        : preForeclosureItem.lotSizeSqFt
                          ? formatNumberCommas(preForeclosureItem.lotSizeSqFt)
                          : "--"}{" "}
                    {preForeclosureItem.lotSizeAcres &&
                    preForeclosureItem.lotSizeAcres > 0.25
                      ? "acres"
                      : preForeclosureItem.lotSizeAcers &&
                          preForeclosureItem.lotSizeAcers > 0.25 //TODO remove when backend is fixed
                        ? "acres"
                        : preForeclosureItem.lotSizeSqFt && "sqft"}
                  </Text>
                </Layout>
              </Layout>

              <Text
                unresponsive
                size="sm"
                textWrap="nowrap"
                passThroughClassess="pre-foreclosure-card__body__address"
              >
                {convertAddressToTitleCase(preForeclosureItem.fullAddress)}
              </Text>
              <Text unresponsive size="sm" fontWeight="bold">
                {preForeclosureItem.landUseDescription
                  ? fullCapsText(preForeclosureItem.landUseDescription)
                  : "N/A"}
              </Text>
            </Layout>
          </Layout>
        </Padding>
      </Container>
    </Container>
  );
};

export default PreForeclosureCard;
